import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, AlertCircle, Package, Layers } from 'lucide-react';
import { fairManagerApi, fairApi } from '../services/api';
import { SlotWithReservation, Fair } from '../types';
import SlotFormModal from './SlotFormModal';
import BulkSlotCreateModal from './BulkSlotCreateModal';
import SlotDetailsModal from './SlotDetailsModal';
import SlotPriceOverrideModal from './SlotPriceOverrideModal';
import SlotFilters from './SlotFilters';
import SlotTable from './SlotTable';
import FairManagerSlotCard from './FairManagerSlotCard';
import { showError } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils';

export default function FairManagerSlotManagement() {
  const { fairId } = useParams<{ fairId: string }>();

  const [fair, setFair] = useState<Fair | null>(null);
  const [slots, setSlots] = useState<SlotWithReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'reserved' | 'pending'>('all');
  const [slotTypeFilter, setSlotTypeFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPriceOverrideModal, setShowPriceOverrideModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<SlotWithReservation | null>(null);

  const loadFair = useCallback(async () => {
    if (!fairId) return;
    try {
      const data = await fairApi.getById(parseInt(fairId));
      setFair(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load fair:', apiError);
      setError(apiError.message);
    }
  }, [fairId]);

  const loadSlots = useCallback(async () => {
    if (!fairId) return;
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        page_size: pageSize
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (slotTypeFilter !== 'all') {
        params.slot_type = slotTypeFilter;
      }

      const data = await fairManagerApi.getSlotsWithReservations(parseInt(fairId), params);
      setSlots(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load slots:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [fairId, page, pageSize, statusFilter, slotTypeFilter]);

  // Load fair details
  useEffect(() => {
    loadFair();
  }, [loadFair]);

  // Load slots when filters or page changes
  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const handleDeleteSlot = async (slotId: number) => {
    try {
      await fairManagerApi.deleteSlot(slotId);
      setShowDeleteModal(false);
      setSelectedSlot(null);
      loadSlots();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to delete slot:', apiError);
      showError('Failed to delete slot', {
        description: apiError.message,
      });
    }
  };

  if (loading && slots.length === 0) {
    return (
      <div className="text-center py-8" data-testid="slot-management-loading">
        <p className="text-gray-500">Loading slot information...</p>
      </div>
    );
  }

  if (error && slots.length === 0) {
    return (
      <div className="text-center py-8" data-testid="slot-management-error">
        <div className="flex items-center justify-center gap-2 text-red-500 mb-4">
          <AlertCircle className="h-5 w-5" />
          <p data-testid="slot-management-error-message">{error}</p>
        </div>
        <button
          onClick={loadSlots}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          data-testid="slot-management-try-again-button"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full max-w-full overflow-hidden">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-3">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base sm:text-xl font-bold text-gray-900" data-testid="slot-management-heading">Manage Slots</h1>
              {fair && <p className="text-sm text-gray-500" data-testid="slot-management-fair-name">{fair.name}</p>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
                data-testid="add-slot-button"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Slot</span>
              </button>
              <button
                onClick={() => setShowBulkCreateModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-teal-500 text-white rounded-lg hover:bg-primary text-sm"
                data-testid="bulk-create-slots-button"
              >
                <Layers className="h-4 w-4" />
                <span className="hidden sm:inline">Bulk Create</span>
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="slot-stats">
            <div className="bg-gray-50 rounded p-2">
              <div className="text-xs text-gray-500">Total Slots</div>
              <div className="text-lg font-semibold text-gray-900" data-testid="total-slots-count">{total}</div>
            </div>
            <div className="bg-green-50 rounded p-2">
              <div className="text-xs text-green-600">Available</div>
              <div className="text-lg font-semibold text-green-700" data-testid="available-slots-count">
                {slots.filter(s => !s.is_reserved).length}
              </div>
            </div>
            <div className="bg-moonbone rounded p-2">
              <div className="text-xs text-secondary">Reserved</div>
              <div className="text-lg font-semibold text-primary-dark" data-testid="reserved-slots-count">
                {slots.filter(s => s.is_reserved && s.reservation?.status === 'confirmed').length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <SlotFilters
        statusFilter={statusFilter}
        slotTypeFilter={slotTypeFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        onSlotTypeFilterChange={(value) => {
          setSlotTypeFilter(value);
          setPage(1);
        }}
      />

      {slots.length === 0 ? (
        <div className="bg-white shadow-sm rounded-lg p-8 text-center" data-testid="slots-empty-state">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4" data-testid="slots-empty-message">No slots found matching your filters.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            data-testid="add-first-slot-button"
          >
            <Plus className="h-4 w-4" />
            Add Your First Slot
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <SlotTable
            slots={slots}
            onViewDetails={(slot) => {
              setSelectedSlot(slot);
              setShowDetailsModal(true);
            }}
            onDelete={(slot) => {
              setSelectedSlot(slot);
              setShowDeleteModal(true);
            }}
            onPriceOverride={(slot) => {
              setSelectedSlot(slot);
              setShowPriceOverrideModal(true);
            }}
          />

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {slots.map((slot) => (
              <FairManagerSlotCard
                key={slot.id}
                slot={slot}
                onViewDetails={(slot) => {
                  setSelectedSlot(slot);
                  setShowDetailsModal(true);
                }}
                onDelete={(slot) => {
                  setSelectedSlot(slot);
                  setShowDeleteModal(true);
                }}
                onPriceOverride={(slot) => {
                  setSelectedSlot(slot);
                  setShowPriceOverrideModal(true);
                }}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white shadow-sm rounded-lg p-3" data-testid="slot-pagination">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500" data-testid="slot-pagination-info">
                  Showing {slots.length} of {total} slots
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    data-testid="slot-pagination-previous-button"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700" data-testid="slot-pagination-page">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    data-testid="slot-pagination-next-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="delete-slot-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900" data-testid="delete-slot-modal-heading">Delete Slot</h3>
            </div>
            <p className="text-gray-700 mb-6" data-testid="delete-slot-modal-message">
              Are you sure you want to delete slot <strong>{selectedSlot.slot_number}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedSlot(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                data-testid="slot-management-delete-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSlot(selectedSlot.id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                data-testid="slot-management-delete-confirm-button"
              >
                Delete Slot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Slot Modal */}
      <SlotFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        fairId={parseInt(fairId!)}
        onSuccess={loadSlots}
      />

      {/* Bulk Create Slots Modal */}
      <BulkSlotCreateModal
        isOpen={showBulkCreateModal}
        onClose={() => setShowBulkCreateModal(false)}
        fairId={parseInt(fairId!)}
        onSuccess={loadSlots}
      />

      {/* Slot Details Modal */}
      <SlotDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSlot(null);
        }}
        slot={selectedSlot}
      />

      {/* Price Override Modal */}
      {selectedSlot && (
        <SlotPriceOverrideModal
          slot={selectedSlot}
          isOpen={showPriceOverrideModal}
          onClose={() => {
            setShowPriceOverrideModal(false);
            setSelectedSlot(null);
          }}
          onSuccess={() => {
            setShowPriceOverrideModal(false);
            setSelectedSlot(null);
            loadSlots(); // Reload to show updated price
          }}
        />
      )}
    </div>
  );
}
